import { useAuth0 } from '@auth0/auth0-react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import { Layout } from './components';
import { PoolList, PoolDetail, EntryForm } from './pages';
import { HomePage } from './pages/HomePage';
import { Profile } from './pages/Profile';
import { SettleUp } from './pages/SettleUp';
import { KnockoutEntryForm } from './pages/KnockoutEntryForm';
import { Pick6EntryForm } from './pages/Pick6EntryForm';
import { F1EntryForm } from './pages/F1EntryForm';
import { WorldCupPick6EntryForm } from './pages/WorldCupPick6EntryForm';
import { WorldCupSurvivorEntryForm } from './pages/WorldCupSurvivorEntryForm';
import { NflNoRepeatEntryForm } from './pages/NflNoRepeatEntryForm';
import { AdminDashboard, AdminImport, AdminResults, AdminCreatePool, AdminKnockoutResults, AdminPick6Results, AdminPayments, AdminFeedback, AdminTeamEditor, AdminBracket, AdminPlayoffSetup, AdminStats, AdminPlayerManager, AdminWorldCup, AdminBroadcast } from './pages/admin';

function CallbackPage() {
  const { isLoading, error, isAuthenticated, user } = useAuth0();

  // Log callback processing for debugging
  console.log('🔄 Callback Page - Processing Auth0 redirect');
  console.log('   Loading:', isLoading);
  console.log('   Error:', error?.message || 'none');
  console.log('   Authenticated:', isAuthenticated);
  console.log('   User:', user?.email || 'none');

  if (error) {
    console.error('❌ Auth0 Error:', error);
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="mb-6">
            <svg className="w-16 h-16 text-red-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Authentication Error</h2>
          <p className="text-gray-600 mb-6">{error.message}</p>
          <a 
            href="/" 
            className="inline-block px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            Return Home
          </a>
        </div>
      </div>
    );
  }

  if (isLoading) {
    console.log('⏳ Still processing Auth0 callback...');
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Completing authentication...</p>
        </div>
      </div>
    );
  }

  console.log('✅ Auth complete, redirecting to /pools');
  return <Navigate to="/pools" replace />;
}

/**
 * Route guard — redirects to Auth0 login if not authenticated.
 * Wrap any <Route> element that should require login.
 */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      loginWithRedirect({
        appState: { returnTo: window.location.pathname },
      });
    }
  }, [isLoading, isAuthenticated, loginWithRedirect]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null; // Will redirect via the useEffect above
  }

  return <>{children}</>;
}

function AuthErrorHandler({ children }: { children: React.ReactNode }) {
  const { error, loginWithRedirect } = useAuth0();

  useEffect(() => {
    // Handle auth errors that require re-login
    if (error) {
      console.error('🔒 Auth Error:', error);
      const msg = error.message?.toLowerCase() || '';

      const requiresReLogin =
        msg.includes('missing refresh token') ||
        msg.includes('missing_refresh_token') ||
        msg.includes('login_required') ||
        msg.includes('consent_required') ||
        msg.includes('interaction_required') ||
        msg.includes('invalid_grant') ||
        msg.includes('access_denied') ||
        msg.includes('unauthorized') ||
        msg.includes('unknown or invalid refresh token');

      if (requiresReLogin) {
        console.log('🔄 Auth error requires re-login - redirecting...');
        loginWithRedirect({
          appState: { returnTo: window.location.pathname }
        });
      }
    }
  }, [error, loginWithRedirect]);

  return <>{children}</>;
}

function App() {
  return (
    <AuthErrorHandler>
      <BrowserRouter future={{ v7_relativeSplatPath: true }}>
      <Layout>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/pools" element={<PoolList />} />
          <Route path="/auth/callback" element={<CallbackPage />} />

          {/* Authenticated routes — all pool-specific pages require login */}
          <Route path="/pools/:poolId" element={<RequireAuth><PoolDetail /></RequireAuth>} />
          <Route path="/pools/:poolId/join" element={<RequireAuth><EntryForm /></RequireAuth>} />
          <Route path="/pools/:poolId/edit/:entryId" element={<RequireAuth><EntryForm /></RequireAuth>} />
          <Route path="/pools/:poolId/knockout" element={<RequireAuth><KnockoutEntryForm /></RequireAuth>} />
          <Route path="/pools/:poolId/knockout/:entryId" element={<RequireAuth><KnockoutEntryForm /></RequireAuth>} />
          <Route path="/pools/:poolId/pick6" element={<RequireAuth><Pick6EntryForm /></RequireAuth>} />
          <Route path="/pools/:poolId/pick6/:entryId" element={<RequireAuth><Pick6EntryForm /></RequireAuth>} />
          <Route path="/pools/:poolId/f1" element={<RequireAuth><F1EntryForm /></RequireAuth>} />
          <Route path="/pools/:poolId/f1/:entryId" element={<RequireAuth><F1EntryForm /></RequireAuth>} />
          <Route path="/pools/:poolId/wc-pick6" element={<RequireAuth><WorldCupPick6EntryForm /></RequireAuth>} />
          <Route path="/pools/:poolId/wc-pick6/:entryId" element={<RequireAuth><WorldCupPick6EntryForm /></RequireAuth>} />
          <Route path="/pools/:poolId/wc-survivor" element={<RequireAuth><WorldCupSurvivorEntryForm /></RequireAuth>} />
          <Route path="/pools/:poolId/wc-survivor/:entryId" element={<RequireAuth><WorldCupSurvivorEntryForm /></RequireAuth>} />
          <Route path="/pools/:poolId/nfl-no-repeat" element={<RequireAuth><NflNoRepeatEntryForm /></RequireAuth>} />
          <Route path="/pools/:poolId/nfl-no-repeat/:entryId" element={<RequireAuth><NflNoRepeatEntryForm /></RequireAuth>} />
          <Route path="/profile" element={<RequireAuth><Profile /></RequireAuth>} />
          <Route path="/settle-up" element={<RequireAuth><SettleUp /></RequireAuth>} />

          {/* Admin routes — require login (admin role checked server-side) */}
          <Route path="/admin" element={<RequireAuth><AdminDashboard /></RequireAuth>} />
          <Route path="/admin/pools/create" element={<RequireAuth><AdminCreatePool /></RequireAuth>} />
          <Route path="/admin/pools/:poolId/import" element={<RequireAuth><AdminImport /></RequireAuth>} />
          <Route path="/admin/pools/:poolId/results" element={<RequireAuth><AdminResults /></RequireAuth>} />
          <Route path="/admin/pools/:poolId/knockout" element={<RequireAuth><AdminKnockoutResults /></RequireAuth>} />
          <Route path="/admin/pools/:poolId/pick6" element={<RequireAuth><AdminPick6Results /></RequireAuth>} />
          <Route path="/admin/payments" element={<RequireAuth><AdminPayments /></RequireAuth>} />
          <Route path="/admin/feedback" element={<RequireAuth><AdminFeedback /></RequireAuth>} />
          <Route path="/admin/pools/:poolId/teams" element={<RequireAuth><AdminTeamEditor /></RequireAuth>} />
          <Route path="/admin/bracket" element={<RequireAuth><AdminBracket /></RequireAuth>} />
          <Route path="/admin/pools/:poolId/bracket" element={<RequireAuth><AdminBracket /></RequireAuth>} />
          <Route path="/admin/world-cup" element={<RequireAuth><AdminWorldCup /></RequireAuth>} />
          <Route path="/admin/broadcast" element={<RequireAuth><AdminBroadcast /></RequireAuth>} />
          <Route path="/admin/pools/:poolId/playoff-setup" element={<RequireAuth><AdminPlayoffSetup /></RequireAuth>} />
          <Route path="/admin/pools/:poolId/stats" element={<RequireAuth><AdminStats /></RequireAuth>} />
          <Route path="/admin/pools/:poolId/players" element={<RequireAuth><AdminPlayerManager /></RequireAuth>} />
        </Routes>
      </Layout>
    </BrowserRouter>
    </AuthErrorHandler>
  );
}

export default App;
